import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBPermissionGroupSchema, PermissionGroupSchema } from './schemas';

type PermissionGroup = z.infer<typeof PermissionGroupSchema>;
type DBPermissionGroup = z.infer<typeof DBPermissionGroupSchema>;

export const PermissionGroupCodecConfig: TransformerConfig<
  typeof PermissionGroupSchema,
  typeof DBPermissionGroupSchema
> = {
  name: 'PermissionGroup',
  clientSchema: PermissionGroupSchema,
  dbSchema: DBPermissionGroupSchema,
  transformers: [
    // basePermissions: [permissionId] ↔ basePermissions: { permissionId: {} }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['basePermissions'] as const,
        encode: ({ basePermissions }) => ({
          basePermissions: Object.fromEntries(
            basePermissions.map((permission: PermissionGroup['basePermissions'][number]) => [
              permission,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['basePermissions'] as const,
        decode: ({ basePermissions = {} }) => ({
          basePermissions: Object.keys(basePermissions),
        }),
      },
    }),

    // currPermissions: [permissionId] ↔ currPermissions: { permissionId: {} }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['currPermissions'] as const,
        encode: ({ currPermissions }) => ({
          currPermissions: Object.fromEntries(
            currPermissions.map((permission: PermissionGroup['currPermissions'][number]) => [
              permission,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['currPermissions'] as const,
        decode: ({ currPermissions = {} }) => ({
          currPermissions: Object.keys(currPermissions),
        }),
      },
    }),

    // users: [{ userId, name, type }] ↔ users: { userId: { name, type } }
    createTransformer<typeof PermissionGroupSchema, typeof DBPermissionGroupSchema>({
      encoder: {
        sourceFields: ['users'] as const,
        encode: ({ users }) => ({
          users: Object.fromEntries(
            users.map((user: PermissionGroup['users'][number]) => [
              user.userId,
              { name: user.name, type: user.type },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['users'] as const,
        decode: ({ users = {} }) => ({
          users: Object.entries(users).map(([userId, userData]) => ({
            userId,
            ...(userData as NonNullable<DBPermissionGroup['users']>[string]),
          })),
        }),
      },
    }),
  ],
};

export const encodePermissionGroupToDb = createEncodeToDbFn(PermissionGroupCodecConfig);
export const decodePermissionGroupFromDb = createDecodeFromDbFn(PermissionGroupCodecConfig);
